import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {HelpIcon} from '@/components/help-icon'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {
  usePositionDetail,
  useSetOfficialDescripcion,
  useTagPeople,
  type Tag as TagType,
  type TagKind,
  type TagPerson,
} from '@/hooks/empresa/organizacion/use-tags'

export function TagPeopleList({
  businessId,
  kind,
  tagId,
  showDescripcion,
}: {
  businessId: string
  kind: TagKind
  tagId: string
  showDescripcion: boolean
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useTagPeople(businessId, kind, tagId, page)

  if (isLoading && !data) return <Skeleton className="h-16 w-full" />
  const people = data?.items ?? []
  if (people.length === 0) {
    return <p className="py-2 text-sm text-muted-foreground">{t('tags.people.empty')}</p>
  }

  return (
    <>
      <ul className="space-y-2 py-2" data-testid="tag-people-list">
        {people.map((person: TagPerson) => (
          <li key={person.id} className="rounded-lg border border-input px-2.5 py-2" data-testid="tag-person-row">
            <p className="text-sm text-foreground">{person.nombre_completo}</p>
            {showDescripcion &&
              (person.cargo_descripcion ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="tag-person-descripcion">
                  «{person.cargo_descripcion}»
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="tag-person-descripcion-empty">
                  {t('tags.people.noDescripcion')}
                </p>
              ))}
          </li>
        ))}
      </ul>
      {data && <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />}
    </>
  )
}

// Once set, the official description always wins; the backend keeps consolidation from overwriting it.
export function PositionDetailDialog({
  businessId,
  position,
  canEdit,
  open,
  onOpenChange,
}: {
  businessId: string
  position: TagType
  canEdit: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const {data: detail, isLoading} = usePositionDetail(businessId, open ? position.id : undefined)
  const setOfficial = useSetOfficialDescripcion(businessId, position.id)
  const [draft, setDraft] = useState('')
  // Ref de callback: Radix monta el contenido después del primer efecto (ver `ui/popover.tsx`).
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)
  const [editing, setEditing] = useState(false)

  function startEditing() {
    setDraft(detail?.official_descripcion ?? '')
    setEditing(true)
  }

  // Consolidación §6b: adopting only pre-fills the editor; the owner decides, never automatic.
  function startAdopting() {
    setDraft(detail?.consolidated_descripcion ?? '')
    setEditing(true)
  }

  function save() {
    setOfficial.mutate(draft.trim() || null, {onSuccess: () => setEditing(false)})
  }

  function clear() {
    setOfficial.mutate(null, {onSuccess: () => setEditing(false)})
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setContenedor} className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{position.name}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <h3 className="font-heading text-sm text-foreground">{t('tags.detail.official')}</h3>
              {editing ? (
                <div className="space-y-2">
                  <Textarea
                    autoFocus
                    minRows={3}
                    maxLength={2000}
                    value={draft}
                    placeholder={t('tags.detail.officialPlaceholder')}
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="official-descripcion-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={setOfficial.isPending}
                      data-testid="official-descripcion-save"
                      onClick={save}>
                      {t('tags.detail.save')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {detail?.official_descripcion ? (
                    <p className="text-sm text-foreground" data-testid="official-descripcion-text">
                      {detail.official_descripcion}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="official-descripcion-empty">
                      {t('tags.detail.officialEmpty')}
                    </p>
                  )}
                  {canEdit && (
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" data-testid="official-descripcion-fix" onClick={startEditing}>
                        {detail?.official_descripcion ? t('tags.detail.edit') : t('tags.detail.fix')}
                      </Button>
                      {detail?.official_descripcion && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={setOfficial.isPending}
                          data-testid="official-descripcion-clear"
                          onClick={clear}>
                          {t('tags.detail.clear')}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <h3 className="font-heading text-sm text-foreground">{t('tags.detail.consolidated')}</h3>
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('tags.detail.consolidated')})}
                  testId="consolidated-tip"
                  contenedor={contenedor}>
                  {t('tags.detail.consolidatedTip')}
                </HelpIcon>
              </div>
              {detail?.consolidated_descripcion ? (
                <>
                  <p className="text-sm text-muted-foreground" data-testid="consolidated-descripcion-text">
                    {detail.consolidated_descripcion}
                  </p>
                  {canEdit && !editing && detail.consolidated_descripcion !== detail.official_descripcion && (
                    <div className="pt-1">
                      <Button size="sm" variant="outline" data-testid="consolidated-adopt" onClick={startAdopting}>
                        {t('tags.detail.adopt')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="consolidated-descripcion-empty">
                  {t('tags.detail.consolidatedEmpty')}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <h3 className="font-heading text-sm text-foreground">
                {t('tags.detail.sources', {count: position.usage_count})}
              </h3>
              <TagPeopleList businessId={businessId} kind="positions" tagId={position.id} showDescripcion />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
